import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useTermsAcceptance } from "@/hooks/useTermsAcceptance";
import { useToast } from "@/hooks/use-toast";

interface TermsAcceptanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requiredType: "renter" | "host" | "both";
  onAccepted?: () => void;
}

export default function TermsAcceptanceDialog({
  open,
  onOpenChange,
  requiredType,
  onAccepted,
}: TermsAcceptanceDialogProps) {
  const { acceptTerms, isAccepting, hasAcceptedRenter, hasAcceptedHost } = useTermsAcceptance();
  const { toast } = useToast();
  
  const [acceptedRenter, setAcceptedRenter] = useState(false);
  const [acceptedHost, setAcceptedHost] = useState(false);

  const needsRenter = requiredType === "renter" || requiredType === "both";
  const needsHost = requiredType === "host" || requiredType === "both";

  const handleAccept = async () => {
    try {
      const promises: Promise<void>[] = [];
      
      if (needsRenter && !hasAcceptedRenter && acceptedRenter) {
        promises.push(acceptTerms("renter"));
      }
      
      if (needsHost && !hasAcceptedHost && acceptedHost) {
        promises.push(acceptTerms("host"));
      }

      if (promises.length === 0) {
        toast({
          title: "Acceptér vilkår",
          description: "Du skal acceptere de relevante vilkår for at fortsætte",
          variant: "destructive",
        });
        return;
      }

      await Promise.all(promises);
      
      toast({
        title: "Vilkår accepteret",
        description: "Du kan nu fortsætte",
      });
      
      onOpenChange(false);
      onAccepted?.();
    } catch (error: any) {
      console.error("[TermsAcceptanceDialog] Error in handleAccept:", error);
      toast({
        title: "Fejl",
        description: error.message || "Kunne ikke gemme accept",
        variant: "destructive",
      });
    }
  };

  const canProceed = 
    (!needsRenter || hasAcceptedRenter || acceptedRenter) &&
    (!needsHost || hasAcceptedHost || acceptedHost);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Retningslinjer for brug af platformen</DialogTitle>
          <DialogDescription>
            Læs venligst følgende retningslinjer grundigt og acceptér dem for at fortsætte
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-6">
            {/* Introduktion */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Velkommen til vores parkeringsdeleplatform. For at sikre en tryg og fair oplevelse for alle
                parter, har vi udarbejdet følgende retningslinjer. Platformen fungerer som formidler mellem
                udlejere og lejere, men er ikke part i selve lejeaftalen.
              </p>
            </div>

            <Separator />

            {/* Lejer retningslinjer */}
            {needsRenter && (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg mb-2">Retningslinjer for lejere</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Når du lejer en parkeringsplads gennem vores platform, accepterer du følgende:
                  </p>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="space-y-2">
                    <h4 className="font-medium">1. Forsikring og ansvar</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Du erklærer, at dit køretøj er lovligt registreret og dækket af gyldig ansvarsforsikring</li>
                      <li>Platformen hæfter ikke for skader, tyveri, ulykker eller tab under parkering, medmindre det skyldes grov uagtsomhed fra platformens side</li>
                      <li>Eventuelle tvister om skader eller tab afgøres direkte mellem dig og udlejer</li>
                      <li>Platformen kan bistå med kontaktformidling, men er ikke erstatningspligtig</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">2. Rettidig afhentning og overskridelse</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Du forpligter dig til at afhente dit køretøj senest ved den aftalte sluttid</li>
                      <li>Ved overskridelse af bookingtiden kan udlejer opkræve et gebyr på 50 kr. pr. påbegyndt time</li>
                      <li>Overskridelse skal dokumenteres via appens tidslog (check-in/check-out funktion)</li>
                      <li>Gentagne overskridelser kan føre til suspension af din konto</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">3. Erstatning for mistet fortjeneste</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Hvis din overskridelse medfører, at udlejer mister en efterfølgende booking, kan udlejer kræve erstatning</li>
                      <li>Erstatningen skal dokumenteres (f.eks. via annulleret booking i appen)</li>
                      <li>Tvister om erstatning behandles gennem platformens klageprocedure</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">4. Brug af parkeringspladsen</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Du må kun parkere det køretøj, der er angivet i bookingen</li>
                      <li>Respektér udlejers specifikke regler og begrænsninger for pladsen</li>
                      <li>Efterlad pladsen i samme stand, som du modtog den</li>
                      <li>Bloker ikke adgangsveje eller andre parkeringspladser</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">5. Klageprocedure</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Klager skal indgives gennem platformens supportsystem inden for 48 timer efter bookingen</li>
                      <li>Vi bestræber os på at behandle klager inden for 5 hverdage</li>
                      <li>Begge parter skal samarbejde og fremlægge relevant dokumentation</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {needsRenter && needsHost && <Separator />}

            {/* Udlejer retningslinjer */}
            {needsHost && (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg mb-2">Retningslinjer for udlejere</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Når du udlejer en parkeringsplads gennem vores platform, accepterer du følgende:
                  </p>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="space-y-2">
                    <h4 className="font-medium">1. Forsikring og lovlighed</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Du erklærer, at parkeringspladsen er lovligt ejet eller disponeret, og at du har ret til at udleje den</li>
                      <li>Du har den nødvendige forsikring, som dækker eventuelle skader i forbindelse med udlejning</li>
                      <li>Platformen hæfter ikke for skader på din ejendom, medmindre det skyldes grov uagtsomhed fra platformens side</li>
                      <li>Du er ansvarlig for at overholde lokale regler og bestemmelser vedrørende udlejning af parkeringspladser</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">2. Tilgængelighed og adgang</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Du sikrer, at pladsen er tilgængelig og klar til brug ved den aftalte starttid</li>
                      <li>Adgangsinformation skal være klar og præcis (koder, nøgler, vejledning)</li>
                      <li>Ved ændringer i tilgængelighed skal du straks informere lejer og platformen</li>
                      <li>Manglende tilgængelighed kan føre til fuld refundering til lejer og potentiel kompensation</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">3. Præcis beskrivelse</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Din annonce skal indeholde korrekte oplysninger om pladsens størrelse, faciliteter og begrænsninger</li>
                      <li>Billeder skal være opdaterede og vise pladsens faktiske tilstand</li>
                      <li>Vildledende information kan føre til annullering uden refundering og suspension af din konto</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">4. Håndtering af overskridelser</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Ved overskridelse af bookingtiden kan du opkræve 50 kr. pr. påbegyndt time</li>
                      <li>Overskridelsen skal dokumenteres via appens check-in/check-out system</li>
                      <li>Du skal give lejer rimelig besked, før du kræver gebyrer eller kontakter myndigheder</li>
                      <li>Ved dokumenteret tab af efterfølgende booking kan du kræve erstatning</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">5. Betaling og afgifter</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Platformen opkræver et servicegebyr på 10% af lejeprisen</li>
                      <li>Udbetalinger sker via Stripe Connect til din registrerede konto</li>
                      <li>Du er ansvarlig for at opgive eventuel indtægt til skattemyndighederne</li>
                      <li>Betalingsprocessen håndteres sikkert af platformen</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">6. Afbestilling</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Du kan ikke afbestille bekræftede bookinger uden gyldig grund</li>
                      <li>Ved afbestilling fra din side får lejer fuld refundering</li>
                      <li>Gentagne afbestillinger kan føre til suspension eller lukning af din konto</li>
                      <li>I nødsituationer kontakt venligst support hurtigst muligt</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">7. Klageprocedure</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                      <li>Klager skal indgives gennem platformens supportsystem inden for 48 timer efter bookingen</li>
                      <li>Vi bestræber os på at behandle klager inden for 5 hverdage</li>
                      <li>Ved dokumenterede skader på din ejendom, kontakt først lejer direkte via appen</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <Separator />

            {/* Fælles vilkår */}
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg mb-2">Fælles vilkår for alle brugere</h3>
              </div>

              <div className="space-y-3 text-sm">
                <div className="space-y-2">
                  <h4 className="font-medium">Platformens rolle</h4>
                  <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                    <li>Platformen fungerer udelukkende som formidler og er ikke part i lejeaftalen</li>
                    <li>Vi faciliterer kontakt, betaling og kommunikation mellem parterne</li>
                    <li>Vi forbeholder os ret til at suspendere eller lukke konti ved overtrædelse af retningslinjerne</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">Kommunikation</h4>
                  <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                    <li>Al kommunikation skal foregå respektfuldt og professionelt</li>
                    <li>Udveksling af kontaktinformation uden for platformen sker på eget ansvar</li>
                    <li>Chikane, trusler eller upassende adfærd tolereres ikke</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">Ændringer i retningslinjer</h4>
                  <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                    <li>Vi forbeholder os ret til at opdatere disse retningslinjer</li>
                    <li>Væsentlige ændringer vil blive kommunikeret via email og i appen</li>
                    <li>Fortsat brug af platformen udgør accept af opdaterede retningslinjer</li>
                  </ul>
                </div>
              </div>
            </div>

            <Separator />

            {/* Accept erklæring */}
            <div className="bg-muted/50 p-4 rounded-lg space-y-4">
              <p className="text-sm font-medium">
                Ved at acceptere disse retningslinjer bekræfter du:
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 ml-4">
                <li>• At du har læst og forstået alle punkter i retningslinjerne</li>
                <li>• At du forpligter dig til at overholde disse retningslinjer</li>
                <li>• At du accepterer platformens rolle som formidler og ikke part i lejeaftalen</li>
                <li>• At du forstår konsekvenserne ved overtrædelse af retningslinjerne</li>
              </ul>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-4 pt-4">
          <div className="flex flex-col gap-3 flex-1">
            {needsRenter && !hasAcceptedRenter && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="accept-renter"
                  checked={acceptedRenter}
                  onCheckedChange={(checked) => setAcceptedRenter(checked === true)}
                />
                <label
                  htmlFor="accept-renter"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Jeg accepterer retningslinjerne for lejere
                </label>
              </div>
            )}
            
            {needsHost && !hasAcceptedHost && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="accept-host"
                  checked={acceptedHost}
                  onCheckedChange={(checked) => setAcceptedHost(checked === true)}
                />
                <label
                  htmlFor="accept-host"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Jeg accepterer retningslinjerne for udlejere
                </label>
              </div>
            )}
          </div>

          <div className="flex gap-2 sm:ml-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isAccepting}>
              Annuller
            </Button>
            <Button onClick={handleAccept} disabled={!canProceed || isAccepting}>
              {isAccepting ? "Gemmer..." : "Acceptér og fortsæt"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
